import {
  isValidPhoneNumber,
  validatePhoneNumberLength,
  parsePhoneNumberFromString,
  AsYouType,
} from "libphonenumber-js/max";

export const digitsOnly = (value: string | null | undefined) =>
  (value ?? "").replace(/\D/g, "");

export const sanitizeDigitsAndSpaces = (value: string) =>
  value.replace(/[^\d\s]/g, "").replace(/\s+/g, " ").trim();

export type PhoneValidationResult =
  | { ok: true; e164: string; country?: string }
  | { ok: false; message: string };

type PhoneLengthError =
  | "TOO_SHORT"
  | "TOO_LONG"
  | "INVALID_LENGTH"
  | "NOT_A_NUMBER"
  | "INVALID_COUNTRY"
  | "IS_POSSIBLE_LOCAL_ONLY"
  | string;

const lengthErrorToMessage = (reason: PhoneLengthError) => {
  switch (reason) {
    case "TOO_SHORT":
      return "El telefono es demasiado corto para el pais seleccionado.";
    case "TOO_LONG":
      return "El telefono es demasiado largo para el pais seleccionado.";
    case "INVALID_LENGTH":
      return "El telefono tiene una longitud invalida para el pais seleccionado.";
    case "NOT_A_NUMBER":
      return "Ingresa un telefono valido (solo digitos).";
    case "INVALID_COUNTRY":
      return "El pais seleccionado no es valido.";
    case "IS_POSSIBLE_LOCAL_ONLY":
      return "El telefono parece ser solo local y no valido en formato internacional.";
    default:
      return "El telefono no tiene una longitud valida para el pais seleccionado.";
  }
};

export const validateInternationalPhone = (
  countryCodeRaw: string,
  nationalRaw: string
): PhoneValidationResult => {
  const countryDigits = digitsOnly(countryCodeRaw);
  const nationalDigits = digitsOnly(nationalRaw);

  if (!countryDigits) return { ok: false, message: "Selecciona un pais." };
  if (!nationalDigits) return { ok: false, message: "Ingresa codigo de area y numero." };

  const candidate = `+${countryDigits}${nationalDigits}`;

  let lengthReason: string | undefined;
  try {
    lengthReason = validatePhoneNumberLength(candidate) as string | undefined;
  } catch {
    return { ok: false, message: "El telefono no se pudo interpretar. Revisa el numero ingresado." };
  }

  if (lengthReason) return { ok: false, message: lengthErrorToMessage(lengthReason) };

  const isValid = isValidPhoneNumber(candidate);
  if (!isValid) return { ok: false, message: "El telefono no es valido para el pais seleccionado." };

  const parsed = parsePhoneNumberFromString(candidate);
  return {
    ok: true,
    e164: parsed?.number ?? candidate,
    country: parsed?.country ?? undefined,
  };
};

export const formatInternationalPhoneForSummary = (
  countryCallingCode?: string | null,
  nationalNumber?: string | null
) => {
  const cc = digitsOnly(countryCallingCode ?? "");
  const nn = digitsOnly(nationalNumber ?? "");

  if (!cc && !nn) return "";
  if (cc && !nn) return `+${cc}`;
  if (!cc && nn) return nn;

  const candidate = `+${cc}${nn}`;
  const parsed = parsePhoneNumberFromString(candidate);
  return parsed?.formatInternational() ?? `+${cc} ${nn}`;
};

export const formatNationalPhoneForInput = (
  countryCallingCodeRaw: string,
  nationalRaw: string
): string => {
  const cc = digitsOnly(countryCallingCodeRaw);
  const nn = digitsOnly(nationalRaw);

  if (!cc || !nn) return sanitizeDigitsAndSpaces(nationalRaw);

  const candidate = `+${cc}${nn}`;
  const parsed = parsePhoneNumberFromString(candidate);
  if (!parsed) return sanitizeDigitsAndSpaces(nationalRaw);

  const intl = parsed.formatInternational();
  const local = intl.replace(new RegExp(`^\\+${cc}\\s*`), "").trim();

  return local.replace(/[^\d\s]/g, " ").replace(/\s+/g, " ").trim();
};

export const formatNationalPhoneAsYouType = (countryCallingCode: string, nationalRaw: string) => {
  const cc = digitsOnly(countryCallingCode);
  const nn = digitsOnly(nationalRaw);

  if (!cc) return sanitizeDigitsAndSpaces(nationalRaw);
  if (!nn) return "";

  const formattedIntl = new AsYouType().input(`+${cc}${nn}`);
  const local = formattedIntl.replace(new RegExp(`^\\+${cc}\\s*`), "").trim();

  return local.replace(/[^\d\s]/g, " ").replace(/\s+/g, " ").trim();
};

export const countDigits = (s: string) => (s.match(/\d/g) ?? []).length;

export const caretPosFromDigitIndex = (formatted: string, digitIndex: number) => {
  if (digitIndex <= 0) return 0;

  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) seen++;
    if (seen >= digitIndex) return i + 1;
  }

  return formatted.length;
};
